import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { MessageService } from 'primeng/api';
import { catchError, throwError } from 'rxjs';

/**
 * Shows a PrimeNG toast for any non-success HTTP error.
 * Matches the backend envelope: { success, message, data, errors }.
 * For 422 the first validation message (errors.<field>[0]) is surfaced.
 * 401 also gets a toast here; auth.interceptor clears the session in parallel.
 */
export const apiErrorInterceptor: HttpInterceptorFn = (req, next) => {
  const messages = inject(MessageService);

  return next(req).pipe(
    catchError((err: unknown) => {
      if (err instanceof HttpErrorResponse) {
        const body = err.error as
          | { message?: string; errors?: Record<string, string[]> | null }
          | null;

        const summary =
          err.status === 0
            ? 'Network error'
            : err.status === 401
              ? 'Session expired'
              : err.status === 403
                ? 'Forbidden'
                : err.status === 404
                  ? 'Not found'
                  : err.status === 422
                    ? 'Validation error'
                    : err.status >= 500
                      ? 'Server error'
                      : `Error ${err.status}`;

        const firstValidation = body?.errors
          ? Object.values(body.errors)[0]?.[0]
          : undefined;
        const detail =
          firstValidation ?? body?.message ?? err.message ?? 'Something went wrong.';

        messages.add({ severity: 'error', summary, detail, life: 5000 });
      }
      return throwError(() => err);
    }),
  );
};
